import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/services/utils/loggingUtils';

const logger = createLogger('OneSignal');

// Global state to track SDK status
let isOneSignalInitialized = false;
let initializationPromise: Promise<boolean> | null = null;
let permissionCache: { enabled: boolean; timestamp: number } | null = null;
const CACHE_DURATION = 30000; // 30 seconds cache

declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: Array<(OneSignal: any) => void>;
  }
}

export const initializeOneSignal = async (): Promise<boolean> => {
  if (isOneSignalInitialized) {
    return true;
  }

  if (initializationPromise) {
    logger.debug('Initialization already in progress, waiting...');
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      logger.info('Starting OneSignal initialization');
      
      // Detect browser info for Safari-specific handling
      const userAgent = navigator.userAgent;
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      const isMobileSafari = /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent);
      
      // Wait for OneSignal to be available with enhanced detection
      let oneSignalAvailable = false;
      let attempts = 0;
      const maxAttempts = 100;
      
      // Enhanced detection function
      const checkOneSignalAvailability = () => {
        if (window.OneSignal && typeof window.OneSignal.init === 'function') {
          return true;
        }
        
        if (window.OneSignalDeferred && Array.isArray(window.OneSignalDeferred)) {
          return 'deferred';
        }
        
        return false;
      };

      while (!oneSignalAvailable && attempts < maxAttempts) {
        const availability = checkOneSignalAvailability();
        
        if (availability === true) {
          oneSignalAvailable = true;
          break;
        }
        
        if (availability === 'deferred' && attempts > 20) {
          // Wait for OneSignal to become available through deferred system
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              resolve();
            }, 2000);
            
            window.OneSignalDeferred!.push((OneSignal: any) => {
              clearTimeout(timeout);
              window.OneSignal = OneSignal;
              oneSignalAvailable = true;
              resolve();
            });
          });
          
          if (oneSignalAvailable) break;
        }

        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }

      if (!oneSignalAvailable || !window.OneSignal) {
        logger.error('OneSignal SDK not available after waiting');
        return false;
      }

      // Only enable debug logging in development
      if (import.meta.env.DEV) {
        window.OneSignal.Debug.setLogLevel('info');
      }

      // Initialize OneSignal with basic options
      const initOptions: any = {
        appId: '046cd40f-664e-462b-aedd-8863a2a20f8d',
        safari_web_id: 'web.onesignal.auto.4a5d2e90-2e3f-43f0-a970-b0d5c1f8f1d5',
        allowLocalhostAsSecureOrigin: import.meta.env.DEV,
      };

      // Add Safari-specific configuration
      if (isSafari || isMobileSafari) {
        const isPWA = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
        
        initOptions.promptOptions = {
          slidedown: {
            prompts: [
              {
                type: "push",
                autoPrompt: false,
                text: {
                  actionMessage: "We'd like to show you notifications for new swap offers and messages.",
                  acceptButton: "Allow",
                  cancelButton: "No Thanks"
                }
              }
            ]
          }
        };
        
        // Safari PWA specific service worker configuration
        if (isPWA) {
          initOptions.serviceWorkerPath = '/OneSignalSDKWorker.js';
          initOptions.serviceWorkerParam = {
            scope: '/',
            registrationScope: '/'
          };
        } else {
          // Regular Safari browser
          initOptions.serviceWorkerParam = {
            scope: '/'
          };
        }
      }

      // Initialize OneSignal
      await window.OneSignal.init(initOptions);

      logger.info('OneSignal initialized successfully');
      isOneSignalInitialized = true;
      return true;
    } catch (error) {
      logger.error('Error initializing OneSignal:', error);
      return false;
    }
  })();

  return initializationPromise;
};

export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    logger.info('Requesting notification permission');
    
    // Check if notifications are supported
    if (!('Notification' in window)) {
      logger.warn('Browser does not support notifications');
      return false;
    }
    
    // Check current permission state
    const currentPermission = Notification.permission;
    
    if (currentPermission === 'denied') {
      return false;
    }
    
    if (currentPermission === 'granted') {
      permissionCache = null;
      return true;
    }
    
    // First try to initialize OneSignal
    const initialized = await initializeOneSignal();
    if (!initialized) {
      logger.warn('OneSignal initialization failed, using browser fallback');
      return await requestBrowserNotificationPermission();
    }

    try {
      // Safari-specific handling
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isMobileSafari) {
        // For mobile Safari, check if we're in a secure context
        if (!window.isSecureContext) {
          logger.error('Not in secure context - required for Safari push notifications');
          return false;
        }
      } else {
        // Try browser native permission first for non-Safari browsers
        const browserPermission = await requestBrowserNotificationPermission();
        if (browserPermission) {
          return true;
        }
      }
      
      // Try OneSignal's notification permission request
      const permission = await window.OneSignal.Notifications.requestPermission();
      
      if (permission) {
        logger.info('Notification permission granted via OneSignal');
        permissionCache = null;
        return true;
      }
    } catch (oneSignalError) {
      logger.warn('OneSignal permission request failed, using browser fallback');
      
      // Only use browser fallback for non-mobile Safari
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (!isMobileSafari) {
        return await requestBrowserNotificationPermission();
      }
    }
    
    return false;
  } catch (error) {
    logger.error('Error requesting notification permission:', error);
    return false;
  }
};

const requestBrowserNotificationPermission = async (): Promise<boolean> => {
  try {
    // Mobile Safari doesn't support the standard Notification API
    const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isMobileSafari) {
      const isPWA = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
      if (!isPWA) {
        return false;
      }
    }
    
    if (!('Notification' in window)) {
      logger.warn('Browser does not support desktop notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    // Special handling for Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      // Add a small delay for Safari to ensure user interaction context is preserved
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    
    if (granted) {
      // Clear cache to force fresh check
      permissionCache = null;
      
      // For Safari, also test creating a notification to ensure it actually works
      if (isSafari) {
        try {
          const testNotification = new Notification('LabSwApp Notifications Enabled', {
            body: 'You will now receive important updates.',
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'test-notification',
            requireInteraction: false
          });
          
          // Auto-close test notification
          setTimeout(() => {
            testNotification.close();
          }, 3000);
        } catch (testError) {
          logger.warn('Safari test notification failed:', testError);
        }
      }
    }
    
    return granted;
  } catch (error) {
    logger.error('Error requesting browser notification permission:', error);
    return false;
  }
};

export const setOneSignalUserId = async (userId: string): Promise<string | null> => {
  try {
    if (!window.OneSignal) {
      logger.error('OneSignal not initialized');
      return null;
    }

    await window.OneSignal.login(userId);
    
    // Enhanced Safari PWA player ID retrieval
    const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    let playerId = null;
    let attempts = 0;
    const maxAttempts = isMobileSafari || isPWA ? 8 : 3;
    
    while (!playerId && attempts < maxAttempts) {
      try {
        // Method 1: Direct access
        playerId = await window.OneSignal.User.PushSubscription.id;
        if (playerId) {
          break;
        }

        // Method 2: Alternative direct access
        const pushSubscription = await window.OneSignal.User.PushSubscription;
        if (pushSubscription && pushSubscription.id) {
          playerId = pushSubscription.id;
          break;
        }

        // Method 3: Via notification object
        const notificationPermission = await window.OneSignal.Notifications.permission;
        if (notificationPermission) {
          const subscription = await window.OneSignal.User.PushSubscription;
          if (subscription?.id) {
            playerId = subscription.id;
            break;
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      } catch (playerIdError) {
        logger.warn(`Player ID retrieval attempt ${attempts + 1} failed:`, playerIdError);
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
    }

    if (!playerId) {
      logger.warn('Unable to get OneSignal player ID after maximum attempts');
      return null;
    }

    // Save player ID to database
    let dbAttempts = 0;
    const maxDbAttempts = 3;
    
    while (dbAttempts < maxDbAttempts) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ onesignal_player_id: playerId })
          .eq('id', userId);

        if (error) {
          throw error;
        }

        logger.info(`Player ID saved to database successfully: ${playerId}`);
        return playerId;
      } catch (dbError) {
        logger.warn(`Database save attempt ${dbAttempts + 1} failed:`, dbError);
        dbAttempts++;
        
        if (dbAttempts < maxDbAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    logger.error('Failed to save player ID to database after all attempts');
    return playerId; // Return the player ID even if DB save failed
  } catch (error) {
    logger.error('Error setting OneSignal user ID:', error);
    return null;
  }
};

export const setUserTags = async (tags: Record<string, any>): Promise<boolean> => {
  try {
    if (!window.OneSignal) {
      logger.error('OneSignal not initialized');
      return false;
    }

    await window.OneSignal.User.addTags(tags);
    logger.info('User tags set successfully');
    return true;
  } catch (error) {
    logger.error('Error setting user tags:', error);
    return false;
  }
};

const checkNotificationPermission = (): boolean => {
  try {
    // Check browser notification permission check
    const notificationSupported = 'Notification' in window;
    let enabled = false;
    
    if (notificationSupported) {
      enabled = Notification.permission === 'granted';
    }

    return enabled;
  } catch (error) {
    logger.error('Error checking notification permission:', error);
    return false;
  }
};

export const areNotificationsEnabled = (): boolean => {
  // Use cached value if available and not expired
  if (permissionCache && Date.now() - permissionCache.timestamp < CACHE_DURATION) {
    return permissionCache.enabled;
  }

  const enabled = checkNotificationPermission();
  
  // Cache the result
  permissionCache = {
    enabled,
    timestamp: Date.now()
  };

  return enabled;
};

export const sendTestNotification = async (): Promise<boolean> => {
  try {
    if (!window.OneSignal) {
      logger.error('OneSignal not initialized');
      return false;
    }

    // Get current user's player ID
    const playerId = await window.OneSignal.User.PushSubscription.id;
    
    if (!playerId) {
      logger.error('No OneSignal player ID found');
      return false;
    }

    // Send notification via Supabase function
    const { data, error } = await supabase.functions.invoke('send-push-notification', {
      body: {
        userIds: [playerId],
        title: 'Test Notification',
        message: 'This is a test notification from LabSwApp!',
        data: {
          type: 'test',
          timestamp: new Date().toISOString()
        }
      }
    });

    if (error) {
      logger.error('Error sending test notification:', error);
      return false;
    }

    logger.info('Test notification sent successfully');
    return true;
  } catch (error) {
    logger.error('Error sending test notification:', error);
    return false;
  }
};

export const getOneSignalStatus = async () => {
  try {
    if (!window.OneSignal) {
      return {
        initialized: false,
        permission: 'unknown',
        playerId: null,
        supported: false
      };
    }

    const permission = await window.OneSignal.Notifications.permission;
    const playerId = await window.OneSignal.User.PushSubscription.id;
    const supported = 'Notification' in window;

    return {
      initialized: true,
      permission,
      playerId,
      supported
    };
  } catch (error) {
    logger.error('Error getting OneSignal status:', error);
    return {
      initialized: false,
      permission: 'error',
      playerId: null,
      supported: false,
      error: error.message
    };
  }
};

export const refreshOneSignalPlayerId = async (userId: string): Promise<string | null> => {
  try {
    if (!window.OneSignal) {
      logger.error('OneSignal not initialized');
      return null;
    }

    // Force refresh by logging out and back in
    await window.OneSignal.logout();
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return await setOneSignalUserId(userId);
  } catch (error) {
    logger.error('Error refreshing OneSignal player ID:', error);
    return null;
  }
};

// Send push notification via Supabase function
export const sendPushNotification = async (playerIds: string[], title: string, message: string, data?: any): Promise<boolean> => {
  try {
    const { error } = await supabase.functions.invoke('send-push-notification', {
      body: {
        userIds: playerIds,
        title,
        message,
        data
      }
    });

    if (error) {
      logger.error('Error sending push notification:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error sending push notification:', error);
    return false;
  }
};

// Get notification debug info (simplified version without mobile debug)
export const getNotificationDebugInfo = async () => {
  try {
    const status = await getOneSignalStatus();
    const browserPermission = Notification.permission;
    
    return {
      oneSignalStatus: status,
      browserPermission,
      notificationSupported: 'Notification' in window,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    logger.error('Error getting notification debug info:', error);
    return {
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};